import React, { memo } from 'react';
import { Check, TriangleAlert, CircleAlert } from 'lucide-react';

// Full metering's status colors, each always shown with an icon and a word
const TONES = {
  good: { text: 'text-meter-good', fill: 'bg-meter-good', Icon: Check },
  watch: { text: 'text-meter-watch', fill: 'bg-meter-watch', Icon: TriangleAlert },
  problem: { text: 'text-meter-problem', fill: 'bg-meter-problem', Icon: CircleAlert },
};

export const Status = ({ status }) => {
  if (!status) return null;
  const { text, Icon } = TONES[status.tone];
  return (
    <span className={`inline-flex items-center gap-space-1 text-label ${text}`}>
      <Icon size={14} strokeWidth={2.5} aria-hidden="true" />
      {status.word}
    </span>
  );
};

const format = (value, digits) => (value === null || value === undefined ? '–' : value.toFixed(digits).replace('-', '−'));

/**
 * One measurement: its name (and what it means), the value, and a bar between min and max. With
 * `centered`, the bar grows from the middle (for values around zero). A status adds its tone and word.
 */
export const Meter = memo(({ label, hint, value, min, max, unit = 'dB', digits = 1, status, centered = false }) => {
  const known = value !== null && value !== undefined;
  const clamp = (v) => Math.min(Math.max(v, 0), 100);
  const position = known ? clamp(((value - min) / (max - min)) * 100) : 0;
  const tone = status ? TONES[status.tone] : null;
  const fill = tone ? tone.fill : 'bg-ink-soft';
  const barStyle = centered
    ? { left: `${Math.min(position, 50)}%`, width: `${Math.abs(position - 50)}%` }
    : { left: 0, width: `${position}%` };

  return (
    <div className="flex flex-col gap-space-2">
      <div className="flex items-baseline justify-between gap-space-3">
        <span className="flex flex-col">
          <span className="text-small text-ink">{label}</span>
          {hint && <span className="text-caption text-muted">{hint}</span>}
        </span>
        <span className="font-mono text-data text-ink whitespace-nowrap">
          {format(known ? value : null, digits)}{known && unit ? ` ${unit}` : ''}
        </span>
      </div>
      <div className="relative h-2 rounded-radius-pill bg-raised overflow-hidden" aria-hidden="true">
        {centered && <span className="absolute left-1/2 top-0 h-full w-px bg-line" />}
        {known && <span className={`absolute top-0 h-full ${fill}`} style={barStyle} />}
      </div>
      <Status status={status} />
    </div>
  );
});

/** Stereo width and phase correlation (how the left and right channels relate) */
export const StereoField = memo(({ width, correlation, monoStatus }) => (
  <div className="flex flex-col gap-space-5">
    <Meter label="Stereo width" hint="0 is mono; around 1 is wide" value={width} min={0} max={2} unit="" digits={2} />
    <Meter
      label="Phase correlation"
      hint="How well it holds up in mono, from −1 to +1"
      value={correlation}
      min={-1}
      max={1}
      unit=""
      digits={2}
      centered
      status={monoStatus}
    />
  </div>
));

/** Tempo and key, as big readouts */
export const MusicalContext = memo(({ bpm, estimatedKey }) => {
  if (bpm === null && !estimatedKey) return null;
  return (
    <div className="grid grid-cols-2 gap-space-3">
      <div className="flex flex-col gap-space-1 p-space-4 rounded-radius-md bg-base">
        <span className="text-caption text-muted">Tempo</span>
        <span className="font-mono text-data-lg text-ink">{bpm !== null ? `${Math.round(bpm)} BPM` : '–'}</span>
      </div>
      <div className="flex flex-col gap-space-1 p-space-4 rounded-radius-md bg-base">
        <span className="text-caption text-muted">Key (estimated)</span>
        <span className="font-mono text-data-lg text-ink">{estimatedKey || '–'}</span>
      </div>
    </div>
  );
});
